import dayjs, { type Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { STANDARD_DATE_FORMATS } from '../date-time-helpers.constants';
import type { DateInput, FormatOptions } from '../date-time-helpers.type';
import { safeParseDateInput } from '../parsing/parsing';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

/**
 * Returns an **inclusive** ISO end date (YYYY-MM-DD) for a range end boundary.
 *
 * Some APIs represent ranges as:
 * - inclusive-of-day (e.g. ends at `23:59:59Z`), or
 * - exclusive end boundary at midnight (e.g. ends at `00:00:00Z`), in which case we subtract 1 day
 *   so the label reflects the last included day.
 *
 * @param {string} end - End boundary timestamp (typically ISO string) or ISO date
 * @returns {string | null} Inclusive ISO date string (YYYY-MM-DD) or null if parsing fails
 */
const getInclusiveISOEndDate = (end: string): string | null => {
  if (!end) {
    return null;
  }

  // If already a date-only string, treat it as inclusive.
  if (/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return end;
  }

  const parsed = dayjs.utc(end);
  if (!parsed.isValid()) {
    return null;
  }

  const isMidnightUTC =
    parsed.hour() === 0 && parsed.minute() === 0 && parsed.second() === 0 && parsed.millisecond() === 0;

  const inclusive = isMidnightUTC ? parsed.subtract(1, 'day') : parsed;
  return inclusive.format('YYYY-MM-DD');
};

/**
 * This is the core date formatting function
 * It can handle:
 * - Custom format string (uses Dayjs format tokens)
 * - Locale for localization (e.g., 'en', 'es', 'fr')
 * - Target timezone for conversion (e.g., 'America/New_York', 'UTC')
 * - Whether to treat input as UTC
 * - Fallback value when date parsing fails
 * - Whether to show relative time instead of absolute (e.g., '2 hours ago')
 * - Reference date for relative time calculations (defaults to now)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {FormatOptions} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatDate = (dateInput: DateInput, options?: FormatOptions): string => {
  const {
    format = STANDARD_DATE_FORMATS.FULL_DATE,
    locale = 'en',
    timezone,
    utc = false,
    utcInput = false,
    fallback = '',
    relative = false,
    relativeTo,
  } = options || {};

  let date: Dayjs | null = null;

  if (utcInput && typeof dateInput === 'string') {
    const parsed = dayjs.utc(dateInput);
    date = parsed.isValid() ? parsed : null;
  } else {
    date = safeParseDateInput(dateInput);
  }

  if (!date || !date.isValid()) {
    return fallback;
  }

  if (relative) {
    const reference = relativeTo ? safeParseDateInput(relativeTo) : dayjs();

    if (!reference) {
      return fallback;
    }

    return date.from(reference);
  }

  try {
    let formattedDate = date;

    if (utc) {
      formattedDate = formattedDate.utc();
    }

    if (timezone) {
      formattedDate = formattedDate.tz(timezone);
    }

    if (locale !== 'en') {
      formattedDate = formattedDate.locale(locale);
    }

    return formattedDate.format(format);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Formats a date to ISO string format
 *
 * @param {DateInput} dateInput - Date to format
 * @returns {string | null} ISO formatted string or null
 */
const formatToISOString = (dateInput: DateInput): string | null => {
  const date = safeParseDateInput(dateInput);

  if (!date) {
    return null;
  }

  return formatDate(date, { format: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]' });
};

/**
 * Formats a date to ISO date format (YYYY-MM-DD)
 * Function overloads provide better type safety:
 * - When dateInput is non-falsy, return type is guaranteed to be string
 * - When dateInput is falsy, return type is null
 *
 * @param {DateInput} dateInput - Date to format
 * @returns {string | null} ISO formatted date string or null
 */
function formatToISODate(dateInput: string | Date | Dayjs): string;
function formatToISODate(dateInput: null | undefined): null;
function formatToISODate(dateInput: DateInput): string | null;
function formatToISODate(dateInput: DateInput): string | null {
  const date = safeParseDateInput(dateInput);

  if (!date) {
    return null;
  }

  return formatDate(date, { format: 'YYYY-MM-DD' });
}

/**
 * ALIAS FUNCTIONS - Convenient shortcuts for common date formatting scenarios
 */

/**
 * Formats a date using the standardized month-year format (Sep, 2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatMonthDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.MONTH_YEAR });
};

/**
 * Formats a date using the standardized quarter-year format (Q3 2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatQuarterDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.QUARTER_YEAR });
};

/**
 * Formats a date using the standardized year-only format (2024)
 *
 * @param {DateInput} dateInput - Date to format
 * @param {Omit<FormatOptions, 'format'>} [options] - Formatting options
 * @returns {string} Formatted date string
 */
const formatYearDate = (dateInput: DateInput, options?: Omit<FormatOptions, 'format'>): string => {
  return formatDate(dateInput, { ...options, format: STANDARD_DATE_FORMATS.YEAR_ONLY });
};

/**
 * Formats a date range
 *
 * @param {DateInput} startDate - Start date
 * @param {DateInput} endDate - End date
 * @param {FormatOptions} [options] - Formatting options
 * @returns {string} Formatted date range string
 */
const formatDateRange = (startDate: DateInput, endDate: DateInput, options?: FormatOptions): string => {
  const start = safeParseDateInput(startDate);
  const end = safeParseDateInput(endDate);

  if (!start || !end) {
    return '';
  }

  return `${formatDate(start, options)} - ${formatDate(end, options)}`;
};

export {
  formatDate,
  formatDateRange,
  formatMonthDate,
  formatQuarterDate,
  formatToISODate,
  formatToISOString,
  formatYearDate,
  getInclusiveISOEndDate,
};
